import {
  IconDefinition,
  faPen,
  faTrashCan,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './styles.module.css';
import { CarAllocation } from '../../../../../types/carAllocation';
import AllocatedTask from '../../AllocatedTask';
import ToggleSwitch from '../../../../../components/common/ToggleSwitch';
import { useToggleCarAllocationLockMutation } from '../../../../../api/carAllocationApi';
import { useNavigate } from 'react-router-dom';
import { logger } from '../../../../../services/logger';
import { useState } from 'react';
import PolicyRequired from '../../../../../components/common/PolicyRequired';
import { getWorkCategoryIDByName } from '../../../../../services/workCategory';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../../redux/store';
import { registerAllocationIdToDelete } from '../../../../../redux/confirmAllocationTaskDeletionSlice';

type Props = {
  allocations: CarAllocation[];
  onRequestClose?: () => void;
  userId?: number | null;
  carId?: number | null;
};

function AllocationDetails({ allocations, onRequestClose }: Props) {
  const task = allocations[0];
  // 元データが更新されても親コンポーネントで保持しているタスク状況は変化しないのでロック状況だけを反映させる state を持つ
  const [isLocked, setIsLocked] = useState(task.isLocked);
  const [toggleLock] = useToggleCarAllocationLockMutation();
  const navigate = useNavigate();

  if (!task) {
    return null;
  }

  const onToggleLock = () => {
    toggleLock({
      carAllocationId: task.carAllocationId,
      isLocked: !isLocked,
    })
      .unwrap()
      .then(() => {
        setIsLocked(!isLocked);
      })
      .catch(logger.error);
  };
  const onClickEdit = () => {
    navigate(`/contracts/edit/${task.contractId}`);
  };
  const dispatch = useDispatch<AppDispatch>();
  const onClickDelete = () => {
    dispatch(registerAllocationIdToDelete(task));
  };
  const isMinimum = [
    getWorkCategoryIDByName('出発'),
    getWorkCategoryIDByName('到着'),
  ].includes(task.workCategory.id);
  const isLoading = task.workCategory.id === getWorkCategoryIDByName('積込');
  const isDisposingOfLoadStore =
    task.workCategory.id === getWorkCategoryIDByName('処分') &&
    !task.goesStraight;
  const isDisposingOfGoesStraight =
    task.workCategory.id === getWorkCategoryIDByName('処分') &&
    task.goesStraight;
  const isShortened = isLoading || isDisposingOfLoadStore;
  const isStore = task.workCategory.id === getWorkCategoryIDByName('保管');

  return (
    <div className={styles.container}>
      <div className={styles.controlBar}>
        {!(
          isDisposingOfGoesStraight ||
          isShortened ||
          isMinimum ||
          isStore
        ) && (
          <PolicyRequired policy={31}>
            <ToggleSwitch
              label="固定する"
              onChange={onToggleLock}
              checked={isLocked}
            />
          </PolicyRequired>
        )}
        <div className={styles.buttons}>
          {!(isShortened || isMinimum) && (
            <PolicyRequired policy={31}>
              <EditButton onClick={onClickEdit} />
            </PolicyRequired>
          )}
          {!isMinimum && task.isEditable && (
            <PolicyRequired policy={32}>
              <DiscardButton onClick={onClickDelete} />
            </PolicyRequired>
          )}
          <CloseButton onClick={onRequestClose} />
        </div>
      </div>
      {allocations.map((allocation) => (
        <AllocatedTask
          key={allocation.carAllocationId}
          allocation={allocation}
          minimum={isMinimum}
          shortened={isShortened}
        />
      ))}
    </div>
  );
}

export default AllocationDetails;

function EditButton({ onClick }: { onClick?: () => void }) {
  return <Button icon={faPen} onClick={onClick} />;
}
function DiscardButton({ onClick }: { onClick?: () => void }) {
  return <Button icon={faTrashCan} onClick={onClick} />;
}
function CloseButton({ onClick }: { onClick?: () => void }) {
  return <Button icon={faXmark} onClick={onClick} />;
}

function Button({
  icon,
  onClick,
}: {
  icon: IconDefinition;
  onClick?: () => void;
}) {
  return (
    <button type="button" className={styles.button} onClick={onClick}>
      <FontAwesomeIcon icon={icon} />
    </button>
  );
}
